<template>
  <!-- 관리자 예약 상세에서 여는 '예약 시간 변경' 창(같은 날). 직원은 방문 3일 전에도 옮길 수 있다. -->
  <v-dialog :value="true" width="460" content-class="rounded-xl" persistent @keydown.esc="close">
    <v-card>
      <v-card-title>예약 시간 변경</v-card-title>
      <v-divider></v-divider>
      <v-card-subtitle class="black--text text-body-1 font-weight-medium mt-2">
        {{ $getUserDisplayName(reservation.name) }} 예약 · {{ reservation.year }}. {{ reservation.month }}. {{ reservation.day }}. / 지금 {{ currentTime }}<template v-if="currentMaker"> · {{ currentMaker }}</template>
      </v-card-subtitle>

      <v-card-text v-if="phase === 'loading'" class="d-flex align-center px-6">
        <v-progress-circular indeterminate size="22" width="2" color="#7d1832" class="mr-3" />
        빈 시간을 불러오고 있습니다.
      </v-card-text>

      <v-card-text v-else-if="phase === 'failed'" class="error--text px-6">
        빈 시간을 불러오지 못했습니다.
        <v-btn text small color="primary" @click="load">다시 불러오기</v-btn>
      </v-card-text>

      <v-card-text v-else-if="phase === 'blocked'" class="text-body-2 font-weight-bold px-6" style="color: #a53a66">{{ blockedMessage }}</v-card-text>

      <v-card-text v-else-if="!slots.length" class="text-body-2 font-weight-bold px-6" style="color: #a53a66">이 날은 옮길 수 있는 빈 시간이 없습니다.</v-card-text>

      <v-card-text v-else class="px-6">
        <v-select v-model="selectedTime" :items="slots" item-text="time" item-value="time" label="옮길 시간" dense outlined hide-details class="mb-4" :disabled="saving" />
        <p v-if="errorMessage" class="text-body-2 font-weight-bold mt-n2 mb-3" style="color: #a53a66; word-break: keep-all" role="alert">{{ errorMessage }}</p>
        <v-select
          v-if="staffItems.length"
          v-model="selectedStaffId"
          :items="staffItems"
          item-text="label"
          item-value="id"
          label="작가"
          dense
          outlined
          hide-details
          class="mb-4"
          :disabled="saving"
        />
        <p v-if="makerChanges" class="text-body-2 font-weight-bold mb-3" style="color: #a53a66; word-break: keep-all" role="status">
          {{ selectedTime }}에는 지금 작가<template v-if="currentMaker">({{ currentMaker }})</template>가 안 됩니다. 작가가 바뀝니다.
        </p>
        <p class="text-body-2 grey--text text--darken-1 mb-0" style="word-break: keep-all">
          방문 3일 전부터는 손님이 직접 바꿀 수 없어 매장에서만 옮깁니다. 옮기기 전에 작가 일정을 확인해 주세요.<br />
          옮기면 손님에게 새 시간으로 예약 안내 카카오톡이 다시 갑니다.
        </p>
      </v-card-text>

      <v-card-actions class="pb-4">
        <v-spacer />
        <v-btn class="px-4" depressed outlined :disabled="saving" @click="close">닫기</v-btn>
        <v-btn v-if="phase === 'ready' && slots.length" class="px-4 mx-4 white--text" color="#7d1832" depressed :loading="saving" :disabled="!selectedTime" @click="submit">변경</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
// 통신이 끊겨도 '변경'이 도는 채로 갇히지 않게, 이 창의 요청은 이 시간 안에 끝낸다(손님 창과 같은 값).
const REQUEST_TIMEOUT_MS = 15000
const BLOCKED_MESSAGES = {
  NOT_PAID: "결제가 끝난 예약만 시간을 바꿀 수 있습니다.",
  PASSED: "이미 지난 예약은 시간을 바꿀 수 없습니다.",
  UNSUPPORTED: "어느 지점 예약인지 알 수 없어 이 창에서는 옮길 수 없습니다.",
}

export default {
  name: "ReservationTimeChangeDialog",

  props: {
    // 관리자 예약 목록의 한 줄(id가 예약 번호)
    reservation: { type: Object, required: true },
  },

  data: () => ({
    phase: "loading", // loading | ready | blocked | failed
    rule: null,
    slots: [],
    // 서버가 알려 준 지금 예약(시간·작가)
    current: null,
    selectedTime: "",
    selectedStaffId: null,
    saving: false,
    errorMessage: "",
    // 응답이 끊겨 옮겨졌는지 모르는 요청(고른 시간·그 전 시간). 다시 불러올 때마다 확인한다.
    unverified: null,
  }),

  computed: {
    currentTime() {
      return String((this.current && this.current.time) || this.reservation.time || "").slice(0, 5)
    },
    currentMaker() {
      return this.$getUserDisplayName((this.current && this.current.maker) || this.reservation.maker)
    },
    // 고른 시간에 지금 작가가 안 되면 작가가 바뀐다.
    makerChanges() {
      const staff = this.selectedSlot ? this.selectedSlot.staff : []
      return staff.length > 0 && !staff.some((item) => item.current)
    },
    selectedSlot() {
      return this.slots.find((slot) => slot.time === this.selectedTime) || null
    },
    staffItems() {
      const staff = this.selectedSlot ? this.selectedSlot.staff : []
      return staff.map((item) => ({ id: item.id, label: this.$getUserDisplayName(item.name) + (item.current ? " (지금 작가)" : "") }))
    },
    blockedMessage() {
      return BLOCKED_MESSAGES[this.rule && this.rule.reason] || "지금은 시간을 바꿀 수 없는 예약입니다."
    },
  },

  watch: {
    selectedTime() {
      const staff = this.selectedSlot ? this.selectedSlot.staff : []
      const keep = staff.find((item) => item.current)
      this.selectedStaffId = staff.length ? (keep || staff[0]).id : null
    },
  },

  mounted() {
    this.load()
  },

  methods: {
    async load() {
      this.phase = "loading"
      this.errorMessage = ""
      try {
        const { data } = await this.$http.get(`/api/booking/reservations/${this.reservation.id}/time-options`, { timeout: REQUEST_TIMEOUT_MS })
        this.rule = data.rule
        this.slots = Array.isArray(data.slots) ? data.slots : []
        this.current = data.reservation || null
        if (!this.slots.some((slot) => slot.time === this.selectedTime)) this.selectedTime = ""
        this.phase = data.rule && data.rule.canChange ? "ready" : "blocked"
        return this.finishUnverified()
      } catch (error) {
        this.phase = "failed"
        return false
      }
    },

    // 응답이 끊겼던 요청이 실제로 옮겨졌으면(다시 읽은 시간 = 고른 시간) 옮긴 것으로 알리고 닫는다.
    finishUnverified() {
      const pending = this.unverified
      if (!pending || !this.current) return false
      this.unverified = null
      if (String(this.current.time).slice(0, 5) !== pending.wanted) return false
      this.$emit("changed", { id: this.current.id, previousTime: pending.before, time: this.current.time, staffUserId: this.current.staffUserId, maker: this.current.maker, noticeSent: null })
      alert(`${pending.before} → ${this.current.time} 로 옮겨졌습니다. 응답이 늦어 안내 카카오톡을 보냈는지는 확인하지 못했습니다. 필요하면 손님에게 직접 알려 주세요.`)
      this.$emit("close")
      return true
    },

    async submit() {
      if (!this.selectedTime || this.saving) return
      const wanted = this.selectedTime
      const before = this.current ? this.current.time : null
      this.saving = true
      this.errorMessage = ""
      try {
        const { data } = await this.$http.post(
          `/api/booking/reservations/${this.reservation.id}/time`,
          { time: this.selectedTime, staffUserId: this.selectedStaffId },
          { timeout: REQUEST_TIMEOUT_MS }
        )
        const makerNote = this.makerChanges && data.maker ? ` 작가도 바뀌었습니다(바뀐 작가: ${this.$getUserDisplayName(data.maker)}).` : ""
        this.$emit("changed", data)
        alert(`${data.previousTime} → ${data.time} 로 옮겼습니다.` + makerNote + (data.noticeSent ? " 손님에게 안내 카카오톡을 보냈습니다." : " 안내 카카오톡은 보내지 못했습니다(휴대폰 번호가 없거나 보내기 실패). 손님에게 직접 알려 주세요."))
        this.$emit("close")
      } catch (error) {
        // 그사이 빈 시간이 달라졌을 수 있어 목록을 다시 받은 뒤 까닭을 보여 준다.
        const message = error && error.response && error.response.data && error.response.data.message
        // 응답만 끊기고 서버는 옮겼을 수 있다: 다시 불러올 때(지금, 그리고 '다시 불러오기'를 누를 때) 옮겨졌는지 본다
        if (!error.response) this.unverified = { wanted, before }
        const moved = await this.load()
        if (moved) return
        this.errorMessage = message || "시간을 바꾸지 못했습니다. 잠시 뒤 다시 눌러 주세요."
      } finally {
        this.saving = false
      }
    },

    close() {
      if (!this.saving) this.$emit("close")
    },
  },
}
</script>
